import React from 'react';

// --- Main List Data from Screenshot ---
const mainListItems = [
  "Reverse engineering firmware",
  "Uploading malicious firmware (backdoor access)",
  "GPS Spoofing (if GPS module attached):",
  "Simulating false coordinates",
  "Causing misdirection or fake return-to-home",
  "WiFi Deauth Lab: Use deauth tool to disconnect the controller from the drone.",
  "Rogue Controller Lab: Create a fake WiFi AP that mimics the real controller and hijack control.",
  "BLE Sniffing Lab: Capture & replay BLE packets to make the drone perform actions.",
  "Firmware Injection Lab: Modify the drone's firmware to add hidden commands.",
  "GPS Spoofing Lab: Simulate GPS signals and mislead the drone's navigation.",
];

// --- Internal CSS for all styling ---
const internalCSS = `
  /* --- Main Page Container --- */
  .chapter-page-container {
    background-color: #fff; /* White background for the whole page */
    font-family: 'Poppins', 'Open Sans', sans-serif; /* Consistent font */
    position: relative; /* Needed for the wavy bottom */
    overflow-x: hidden; /* Prevent horizontal scroll */
  }

  /* --- 1. Top Banner Section --- */
  .chapter-top-banner {
    position: relative;
    width: 100%;
    height: 350px; 
    overflow: hidden;
    display: flex;
    flex-direction: column; 
    justify-content: center; 
    background-color: #1a1a1a; 
    padding: 1rem 2rem; 
  }

  .chapter-banner-bg-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    opacity: 0.5; /* Dim the image */
  }

  .chapter-banner-content {
    position: relative;
    z-index: 1;
    color: #fff;
    text-align: left;
    max-width: 960px; /* Max width for content */
    margin: 0 auto; /* Center content horizontally */
    width: 100%;
  }

  .chapter-badge {
    background-color: #dc3545; /* Red color */
    color: #fff;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 0.2rem 0.75rem;
    border-radius: 4px;
    display: inline-block; 
    margin-bottom: 0.75rem;
  }

  .chapter-main-title {
    font-size: 2.8rem;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0;
  }

  /* --- 2. List Section --- */
  .chapter-list-section {
    padding: 4rem 1rem;
    background-color: #fff;
    position: relative; 
    z-index: 1;
    min-height: 50vh; /* Ensure there's space if content is short */
  }

  .chapter-list-container {
    max-width: 960px; /* Align with banner content */
    margin: 0 auto;
    width: 100%;
  }

  .chapter-list {
    list-style-type: disc; /* Default bullet style */
    padding-left: 1.5rem; /* Standard indent for list */
  }

  .chapter-list li {
    font-size: 1.15rem;
    line-height: 1.8;
    color: #333;
    margin-bottom: 0.5rem; /* Space between list items */
    font-weight: 500; /* Medium weight, as seen in image */
    padding-left: 0.5rem; /* Add slight indent for bullets */
  }
   /* Styling for nested/indented items */
   .chapter-list li.sub-item {
     padding-left: 2rem; /* Indent sub-items further */
     font-size: 1.1rem; /* Slightly smaller */
     position: relative;
  }
  .chapter-list li.sub-item::before {
     content: '-'; /* Use dash for sub-items */
     position: absolute;
     left: 0.5rem; /* Adjust position */
  }

  /* --- Wavy Bottom Section Removed --- */

  /* --- Responsive Adjustments --- */
  @media (max-width: 991.98px) { /* Tablets */
    .chapter-top-banner {
      height: 300px;
    }
    .chapter-main-title { 
      font-size: 2.4rem;
    }
    .chapter-list li {
      font-size: 1.05rem;
      line-height: 1.7;
    }
  }

  @media (max-width: 767.98px) { /* Mobile Devices */
    .chapter-top-banner {
      height: 130px;
      justify-content: flex-end; 
      padding-bottom: 1.5rem;
      padding-left: 1rem;
      padding-right: 1rem;
    }
    .chapter-banner-content {
      text-align: center; /* Center text on mobile */
    }
    .chapter-badge {
      font-size: 0.8rem;
      padding: 0.15rem 0.5rem;
      margin-bottom: 0.5rem;
    }
    .chapter-main-title {
      font-size: 1.2rem;
      line-height: 1.1;
    }
    .chapter-list-section {
      padding: 2rem 1rem;
    }
    .chapter-list {
      padding-left: 1rem; /* Reduce indent on mobile */
    }
    .chapter-list li {
      font-size: 0.95rem; /* Smaller text on mobile */
      line-height: 1.6;
      margin-bottom: 0.4rem;
      padding-left: 0.25rem;
    }
    .chapter-list li.sub-item {
        padding-left: 1.5rem;
        font-size: 0.9rem;
     }
      .chapter-list li.sub-item::before {
        left: 0.25rem;
      }
  }
`;

const Chapter31ContPage = () => {
   // Helper to add classes based on content structure
  const getItemClass = (item) => {
    if (["GPS Spoofing (if GPS module attached):"].includes(item)) {
      return ''; // Main item, no special class needed for bolding here
    }
    // Items that should be indented
    if (["Simulating false coordinates", "Causing misdirection or fake return-to-home"].includes(item)) {
      return 'sub-item';
    }
    return '';
  };

  return (
    <>
      <style>{internalCSS}</style>
      
      <div className="chapter-page-container">
        
        {/* --- 1. Top Banner Section --- */}
        <div className="chapter-top-banner">
          <img 
            src="https://skillsuprise.com/data/cyberPDF/chapters/chapter31(cont).png" 
            alt="Chapter 31 Background" 
            className="chapter-banner-bg-img" 
          />
          <div className="chapter-banner-content">
            <div>
              {/* Note the "(Cont)" added */ }
              <span className="chapter-badge">Chapter 31 (Cont)</span> 
              <h1 className="chapter-main-title">
                Drone Hacking
              </h1>
            </div>
          </div>
        </div>
        
        {/* --- 2. List Section --- */}
        <div className="chapter-list-section">
          <div className="chapter-list-container">
            
            {/* --- Main Bullet List --- */}
            <ul className="chapter-list">
              {mainListItems.map((item, index) => (
                <li key={index} className={getItemClass(item)}>{item}</li>
              ))}
            </ul>
            
            {/* --- Tools & Outcomes Section Removed --- */}
            
          </div>
        </div>
        
        {/* --- Wavy Bottom SVG Removed --- */}

      </div>
    </>
  );
};

export default Chapter31ContPage;